/*
 * Array of Selectors to record Interaction Events.
 *
 * Example:
 * When we click Go to Course button, send course Id along with default analytic Object.
 * <button data-analytics='student-course-click' />
 * <input id='courseSelect' value='courseId' />
 *
 * {
 *    action: 'Student Course click',
 *    eventAction: 'LaunchCourse',
 *    eventCategory: 'Course',
 *    selector: "[data-analytics='student-course-click']",
 *    tags : [
 *       {
 *           name: 'sectionId',
 *           selector: '[id='courseSelect']',
 *       }
 *   ]
 * }
 *
 *
 * Additional information for an action, like sectionId, AssignmentId, deployment ID are provided in a array of Tags.
 * Each tag has a name and Selector (optional).
 * Selector for a tag are provided only when you want to scrape its value from a DOM element apart from default hidden element
 * Preference Order to Scrape Tag Value:
 * 1. event-> currentTarget-> dataSet[tagName]
 * 2. query DOM using selector -> value
 * 3. Input Hidden Element [wa-analytics-{tagName}] -> value
 *
 * */

export interface IAnalyticsTag {
    name: string;
    selector?: string;
}

export interface IAnalyticsAction {
    action: string;
    eventAction: string;
    eventCategory: string;
    selector: string;
    tags?: IAnalyticsTag[];
}

export const analyticsElements: IAnalyticsAction[] = [
    {
        action: 'Logout',
        eventAction: 'Logout',
        eventCategory: 'Navigation',
        selector: "[data-analytics='signout']",
    },
    {
        action: 'help link',
        eventAction: 'Launch',
        eventCategory: 'Help',
        selector: "[data-analytics='help-launch']",
    },
    {
        action: 'Account Settings link',
        eventAction: 'Launch',
        eventCategory: 'My-Account',
        selector: "[data-analytics='account-settings-launch']",
    },
    {
        action: 'Cengage Dashboard Link',
        eventAction: 'Launch',
        eventCategory: 'Dashboard',
        selector: "[data-analytics='dashboard-launch']",
    },
    {
        action: 'Assignments Nav',
        eventAction: 'Launch',
        eventCategory: 'Assignments-Nav',
        selector: "[data-analytics='assignments-nav']",
    },
    {
        action: 'open communication',
        eventAction: 'Launch',
        eventCategory: 'Communication',
        selector: "[data-analytics='communication-nav']",
    },
    {
        action: 'Student Gradebook open',
        eventAction: 'StudentGradebookOpen',
        eventCategory: 'Grades',
        tags: [
            {
                name: 'sectionId',
            },
        ],
        selector: "[data-analytics='grades-nav']",
    },
    {
        action: 'open calendar',
        eventAction: 'Launch',
        eventCategory: 'Calendar',
        selector: "[data-analytics='calendar-nav']",
    },
    {
        action: 'open home',
        eventAction: 'Launch',
        eventCategory: 'Home',
        selector: "[data-analytics='home-nav']",
    },
    {
        action: 'launch class insights',
        eventAction: 'Launch',
        eventCategory: 'ClassInsights',
        selector: "[data-analytics='view-insights-nav']",
    },
    {
        action: 'launch current assignments',
        eventAction: 'Launch',
        eventCategory: 'CurrentAssignments',
        selector: "[data-analytics='current-assignments-nav']",
    },
    {
        action: 'launch past assignments',
        eventAction: 'Launch',
        eventCategory: 'PastAssignments',
        selector: "[data-analytics='past-assignments-nav']",
    },
    {
        action: 'launch ask teacher',
        eventAction: 'Launch',
        eventCategory: 'Ask-Your-Teacher',
        selector: "[data-analytics='ask-teacher']",
    },
    {
        action: 'launch myEbooks nav',
        eventAction: 'Launch',
        eventCategory: 'My-Ebooks',
        selector: "[data-analytics='ebooks-nav']",
    },
    {
        action: 'Student assignment link clicked',
        eventAction: 'StudentAssignmentLinkClicked',
        eventCategory: 'Assignment',
        tags: [
            {
                name: 'assignmentId',
            },
        ],
        selector: "[data-analytics='student-assignment-link-clicked']",
    },
    {
        action: 'Student assignment link clicked from Calendar page',
        eventAction: 'StudentAssignmentLinkClickedFromCalendar',
        eventCategory: 'Assignment',
        tags: [
            {
                name: 'assignmentId',
            },
        ],
        selector:
            "[data-analytics='student-assignment-link-clicked-from-calendar']",
    },
    {
        action: 'Student course link clicked',
        eventAction: 'StudentCourseLinkClicked',
        eventCategory: 'Course',
        tags: [
            {
                name: 'sectionId',
                selector: "[id='courseSelect']",
            },
        ],
        selector: "[data-analytics='student-course-link-clicked']",
    },
    {
        action: 'Student course page loaded',
        eventAction: 'StudentCoursePageLoaded',
        eventCategory: 'Course',
        selector: "[data-analytics='student-course-page-loaded']",
    },
    {
        action: 'Student assignment page loaded',
        eventAction: 'StudentAssignmentPageLoaded',
        eventCategory: 'Assignment',
        selector: "[data-analytics='student-assignment-page-loaded']",
    },
    {
        action: 'Change language setting',
        eventAction: 'ChangeLanguageSetting',
        eventCategory: 'Navigation',
        selector: "[data-analytics='language_dropdown']",
    },
    {
        action: 'Assignment Restrictions Page Loaded',
        eventAction: 'AssignmentRestrictionsPageLoaded',
        eventCategory: 'Assignment',
        selector: "[data-analytics='assignment-restrictions-page-loaded']",
    },
    {
        action: 'Click LockDown Browser Help Link',
        eventAction: 'OpenLockDownBrowserHelp',
        eventCategory: 'Navigation',
        selector: "[data-analytics='lockdown-browser-help-link']",
    },
    {
        action: 'Click Verify Password Protected',
        eventAction: 'VerifyPasswordProtected',
        eventCategory: 'Assignment',
        selector: "[data-analytics='verify-password-protected']",
    },
    {
        action: 'Click Launch LockDown Browser',
        eventAction: 'LaunchLockDownBrowser',
        eventCategory: 'Assignment',
        selector: "[data-analytics='launch-lockdown-browser']",
    },
    {
        action: 'Click Launch Assignment from Interrupt Page',
        eventAction: 'LaunchAssignment',
        eventCategory: 'Assignment',
        selector: "[data-analytics='launch-assignment-from-interrupt-page']",
    },
    {
        action: 'Click Print Assignment button',
        eventAction: 'PrintAssignment',
        eventCategory: 'Assignment',
        selector: "[data-analytics='print-assignment-button']",
    },
    {
        action: 'Click Request Extension button in Due Date',
        eventAction: 'RequestExtensionDueDate',
        eventCategory: 'Assignment',
        selector: "[data-analytics='request-extension-due-date-button']",
    },
    {
        action: 'Click Request Extension button in Footer',
        eventAction: 'RequestExtensionFooter',
        eventCategory: 'Assignment',
        selector: "[data-analytics='request-extension-footer-button']",
    },
    {
        action: 'Click Request Extension button in Callout Card',
        eventAction: 'RequestExtensionWarningCard',
        eventCategory: 'Assignment',
        selector: "[data-analytics='request-extension-callout-card-button']",
    },
    {
        action: 'Click View Key button',
        eventAction: 'ViewKeyWarningCard',
        eventCategory: 'Assignment',
        selector: "[data-analytics='view-key-button']",
    },
    {
        action: 'Click Details button',
        eventAction: 'DisplayQuestionDetails',
        eventCategory: 'Assignment',
        selector: "[data-analytics='details-button']",
    },
    {
        action: 'Click Previous Answers button',
        eventAction: 'DisplayPreviousAnswers',
        eventCategory: 'Assignment',
        selector: "[data-analytics='previous-answers-button']",
    },
    {
        action: 'Click My Notes button',
        eventAction: 'DisplayMyNotes',
        eventCategory: 'Assignment',
        selector: "[data-analytics='my-notes-button']",
    },
    {
        action: 'Click Ask Your Teacher button',
        eventAction: 'DisplayAskYourTeacher',
        eventCategory: 'Assignment',
        selector: "[data-analytics='ask-your-teacher-button']",
    },
    {
        action: 'Click Practice Another button',
        eventAction: 'DisplayPracticeAnother',
        eventCategory: 'Assignment',
        selector: "[data-analytics='practice-another-button']",
    },
    {
        action: 'Open Learn It',
        eventAction: 'OpenLearnIt',
        eventCategory: 'Assignment',
        selector: "[data-analytics='open_learn_it']",
        tags: [
            { name: 'sectionId' },
            { name: 'deploymentId' },
            { name: 'assignmentId' },
            { name: 'questionId' },
            { name: 'institution' },
        ],
    },
    {
        action: 'Click Past Assignments button on My Assignments Page',
        eventAction: 'ViewPastAssignments',
        eventCategory: 'Course',
        selector: "[data-analytics='show-past-assignments-button']",
    },
    {
        action: 'Click Current Assignments button on My Assignments Page',
        eventAction: 'ViewCurrentAssignments',
        eventCategory: 'Course',
        selector: "[data-analytics='show-current-assignments-button']",
    },
    {
        action: 'Click All Assignments button on My Assignments Page',
        eventAction: 'ViewAllAssignments',
        eventCategory: 'Course',
        selector: "[data-analytics='show-all-assignments-button']",
    },
    {
        action: 'Click Request Extension on My Assignments Page',
        eventAction: 'RequestExtensionMyAssignments',
        eventCategory: 'Assignment',
        selector: "[data-analytics='my-assignments-request-extension']",
        tags: [
            {
                name: 'sectionId',
            },
        ],
    },
    {
        action: 'Click Grading scale link',
        eventAction: 'GradingScaleClick',
        eventCategory: 'Grades',
        selector: "[data-analytics='grading_scale_button']",
    },
    {
        action: 'Graph button clicked for Class Grades on Grades Page',
        eventAction: 'OpenClassGradesHistogram',
        eventCategory: 'Grades',
        selector: "[data-analytics='overall_grades_graph_button']",
    },
    {
        action: 'Click Graph button for Class Scores on Grades Page',
        eventAction: 'OpenClassScoresGraph',
        eventCategory: 'Grades',
        selector: "[data-analytics='class_scores_graph_button']",
    },
    {
        action:
            'Click Graph button for individual assignment for Class Scores on Grades Page',
        eventAction: 'OpenClassScoresGraphIndividual',
        eventCategory: 'Grades',
        selector: "[data-analytics='class_scores_graph_button_individual']",
    },
    {
        action: 'Click on Scores tab on Grades Page',
        eventAction: 'OpenScoresTab',
        eventCategory: 'Grades',
        selector: "[data-analytics='category_scores_tab']",
    },
    {
        action: 'Click on Scores tab on Grades Page',
        eventAction: 'OpenRawScoresTab',
        eventCategory: 'Grades',
        selector: "[data-analytics='raw_scores_tab']",
    },
    {
        action: 'Click on Scores tab on Grades Page',
        eventAction: 'OpenClassScoresTab',
        eventCategory: 'Grades',
        selector: "[data-analytics='class_scores_tab']",
    },
    {
        action: 'Click on Details toggle button',
        eventAction: 'ClickDetailsToggle',
        eventCategory: 'Grades',
        selector: "[data-analytics='score_details_toggle']",
    },
    {
        action: 'Click on Accordion button for Scores tab',
        eventAction: 'OpenScoresAccordion',
        eventCategory: 'Grades',
        selector: "[data-analytics='category_scores_accordion']",
    },
    {
        action: 'Click on Accordion button for Raw Scores tab',
        eventAction: 'OpenRawScoresAccordion',
        eventCategory: 'Grades',
        selector: "[data-analytics='raw_scores_accordion']",
    },
    {
        action: 'Click on Accordion button for Class Scores tab',
        eventAction: 'OpenClassScoresAccordion',
        eventCategory: 'Grades',
        selector: "[data-analytics='class_scores_accordion']",
    },
    {
        action: 'Click Forum link on a Home Page',
        eventAction: 'OpenForumHomePage',
        eventCategory: 'Communication',
        selector: "[data-analytics='forum-home-page-link']",
    },
    {
        action: 'Click Forum link on the Communication Page',
        eventAction: 'OpenForum',
        eventCategory: 'Communication',
        selector: "[data-analytics='open-forum']",
    },
    {
        action: 'Click New Topic top/bottom button on Forum Screen',
        eventAction: 'CreateNewTopic',
        eventCategory: 'Communication',
        selector: "[data-analytics='create-new-topic-button']",
    },
    {
        action: 'Click Save New Topic button',
        eventAction: 'SaveNewTopic',
        eventCategory: 'Communication',
        selector: "[data-analytics='save-new-topic-button']",
    },
    {
        action: 'Click Forum Dropdown',
        eventAction: 'DisplayForumDropdown',
        eventCategory: 'Communication',
        selector: "[data-analytics='forum-dropdown-use']",
    },
    {
        action: 'Click Forum GO button',
        eventAction: 'OpenSelectedForum',
        eventCategory: 'Communication',
        selector: "[data-analytics='open-selected-forum-dropdown']",
    },
    {
        action: 'Click Topic link on the Forum Screen',
        eventAction: 'OpenTopic',
        eventCategory: 'Communication',
        selector: "[data-analytics='open-topic']",
    },
    {
        action: 'Click Breadcrumb Forum link on Topic Screen',
        eventAction: 'ForumBreadcrumb',
        eventCategory: 'Communication',
        selector: "[data-analytics='forum-breadcrumb']",
    },
    {
        action: 'Click Topic Dropdown',
        eventAction: 'DisplayTopicDropdown',
        eventCategory: 'Communication',
        selector: "[data-analytics='topic-dropdown-use']",
    },
    {
        action: 'Click Topic GO button',
        eventAction: 'OpenSelectedTopic',
        eventCategory: 'Communication',
        selector: "[data-analytics='open-selected-topic-dropdown']",
    },
    {
        action: 'Click Send Post button on Topic Screen',
        eventAction: 'SendNewPost',
        eventCategory: 'Communication',
        selector: "[data-analytics='send-new-post']",
    },
    {
        action: 'Click on Archived Announcements link',
        eventAction: 'OpenArchivedAnnouncements',
        eventCategory: 'Announcements',
        selector: "[data-analytics='archived-announcements-link']",
    },
    {
        action: 'Previous month clicked',
        eventAction: 'PreviousMonthClicked',
        eventCategory: 'Calendar',
        selector: "[data-analytics='previous-month-clicked']",
    },
    {
        action: 'Next month clicked',
        eventAction: 'NextMonthClicked',
        eventCategory: 'Calendar',
        selector: "[data-analytics='next-month-clicked']",
    },
    {
        action: 'Download current month button clicked',
        eventAction: 'DownloadMonthButtonClicked',
        eventCategory: 'Calendar',
        selector: "[data-analytics='download-current-month-calendar']",
    },
    {
        action: 'Click on Notifications',
        eventAction: 'NotificationSettings',
        eventCategory: 'Settings',
        selector: "[data-analytics='open-notification-settings']",
    },
    {
        action: 'Click on Save button in Notifications modal',
        eventAction: 'SaveNotificationSettings',
        eventCategory: 'Settings',
        selector: "[data-analytics='save-notification-settings']",
    },
    {
        action: 'Click Go button in Quick search menu',
        eventAction: 'QuickSearchMenu',
        eventCategory: 'Search',
        selector: "[data-analytics='quick-search']",
    },
    {
        action: 'Click Search button in the Questions Search',
        eventAction: 'QuestionsSearchMenu',
        eventCategory: 'Search',
        selector: "[data-analytics='questions-search']",
    },
    {
        action: 'Click Search button in the Question Browser Search',
        eventAction: 'QuestionBrowserSearch',
        eventCategory: 'Search',
        selector: "[data-analytics='question-browser-search']",
    },
    // Private messages events
    {
        action: 'Click Subject link (Threads page)',
        eventAction: 'Launch',
        eventCategory: 'openThread',
        selector: "[data-analytics='open-thread-view']",
    },
    {
        action: 'Click New Message button (Threads page)',
        eventAction: 'Launch',
        eventCategory: 'createNewThread',
        selector: "[data-analytics='open-create-thread-page']",
    },
    {
        action: 'Click Send button (New Thread page)',
        eventAction: 'Complete',
        eventCategory: 'createNewThread',
        selector: "[data-analytics='create-new-thread']",
    },
    {
        action: 'Click Send button (Thread page)',
        eventAction: 'Complete',
        eventCategory: 'createNewMessageInThread',
        selector: "[data-analytics='send-message-in-thread']",
    },
    {
        action: 'Click Settings button (Threads page)',
        eventAction: 'Launch',
        eventCategory: 'threadSettings',
        selector: "[data-analytics='open-settings-window']",
    },
    {
        action: 'Click Apply button (Settings window)',
        eventAction: 'Complete',
        eventCategory: 'threadSettings',
        selector: "[data-analytics='apply-private-messages-settings']",
    },
    // Faculty Nav Bar
    {
        action: 'Click on WebAssign Homepage element',
        eventAction: 'Launch',
        eventCategory: 'homepage',
        selector: "[data-analytics='faculty-header-homepage']",
    },
    {
        action: 'Click on Magnifying Glass icon',
        eventAction: 'Launch',
        eventCategory: 'quickSearch',
        tags: [
            {
                name: 'searchBy',
                selector: "[id='quickSearchDropDownButton']",
            },
        ],
        selector: "[data-analytics='faculty-header-search'] button",
    },
    {
        action: 'Click on View as Student',
        eventAction: 'Launch',
        eventCategory: 'openStudentView',
        selector: "[data-analytics='faculty-header-view-as-student']",
    },
    {
        action: 'Click on Help',
        eventAction: 'Launch',
        eventCategory: 'instructorHelp',
        selector: "[data-analytics='faculty-header-help']",
    },
    {
        action: 'Open User Menu and Click on My Account',
        eventAction: 'Launch',
        eventCategory: 'myAccount',
        selector: "[data-analytics='faculty-header-account']",
    },
    {
        action: 'Open User Menu and Click on My Options',
        eventAction: 'Launch',
        eventCategory: 'myOptions',
        selector: "[data-analytics='faculty-header-options']",
    },
    {
        action: 'Open User Menu and Click on Folders',
        eventAction: 'Launch',
        eventCategory: 'openFolders',
        selector: "[data-analytics='faculty-header-folders']",
    },
    {
        action: 'Open User Menu and Click on Calendar',
        eventAction: 'Launch',
        eventCategory: 'openCalendar',
        selector: "[data-analytics='faculty-header-calendar']",
    },
    {
        action: 'Open User Menu and Click on Sign Out',
        eventAction: 'Logout',
        eventCategory: 'Session',
        selector: "[data-analytics='faculty-header-signout']",
    },
    {
        action: '"Classes" dropdown: click on Create Class',
        eventAction: 'Launch',
        eventCategory: 'createClass',
        selector: "[data-analytics='nav-classes-create-class']",
    },
    {
        action: '"Classes" dropdown: click on My Classes',
        eventAction: 'Launch',
        eventCategory: 'myClasses',
        selector: "[data-analytics='nav-classes-my-classes']",
    },
    {
        action: '"Assignments" dropdown: click on Create',
        eventAction: 'Launch',
        eventCategory: 'createAssignment',
        selector: "[data-analytics='nav-assignments-create-assignment']",
    },
    {
        action: '"Assignments" dropdown: click on My Assignments',
        eventAction: 'Launch',
        eventCategory: 'myAssignments',
        selector: "[data-analytics='nav-assignments-my-assignments']",
    },
    {
        action: '"Assignments" dropdown: click on Settings Templates',
        eventAction: 'Launch',
        eventCategory: 'settingsTemplates',
        selector: "[data-analytics='nav-assignments-settings-templates']",
    },
    {
        action: '"Assignments" dropdown: click on Search Assignments',
        eventAction: 'Launch',
        eventCategory: 'advancedAssignmentSearch',
        selector: "[data-analytics='nav-assignments-search-assignments']",
    },
    {
        action: '"Assignments" dropdown: click on My Permissions Group',
        eventAction: 'Launch',
        eventCategory: 'myPermissionsGroups',
        selector: "[data-analytics='nav-assignments-my-permissions-groups']",
    },
    {
        action: '"Questions" dropdown: click on Create Question',
        eventAction: 'Launch',
        eventCategory: 'createQuestion',
        selector: "[data-analytics='nav-questions-create-questions']",
    },
    {
        action: '"Questions" dropdown: click on My Questions',
        eventAction: 'Launch',
        eventCategory: 'myQuestions',
        selector: "[data-analytics='nav-questions-my-questions']",
    },
    {
        action: '"Questions" dropdown: click on Search',
        eventAction: 'Launch',
        eventCategory: 'advancedQuestionSearch',
        selector: "[data-analytics='nav-questions-search-questions']",
    },
    {
        action: '"Grades" dropdown: click on Gradebook',
        eventAction: 'Launch',
        eventCategory: 'sectionGradebook',
        selector: "[data-analytics='nav-grades-gradebook']",
    },
    {
        action: '"Grades" dropdown: click on Custom Reporting',
        eventAction: 'Launch',
        eventCategory: 'customReporting',
        selector: "[data-analytics='nav-grades-custom-reporting']",
    },
    {
        action: '"Grades" dropdown: click on Class Scores',
        eventAction: 'Launch',
        eventCategory: 'sectionScores',
        selector: "[data-analytics='nav-grades-class-scores']",
    },
    {
        action: '"Grades" dropdown: click on Download Scores',
        eventAction: 'Launch',
        eventCategory: 'downloadScores',
        selector: "[data-analytics='nav-grades-download-scores']",
    },
    {
        action: '"Grades" dropdown: click on Import Scores to Gradebook',
        eventAction: 'Launch',
        eventCategory: 'importScoresToGradebook',
        selector: "[data-analytics='nav-grades-import-scores-to-gradebook']",
    },
    {
        action: '"Grades" dropdown: click on My Categories',
        eventAction: 'Launch',
        eventCategory: 'myCategories',
        selector: "[data-analytics='nav-grades-custom-categories']",
    },
    {
        action: 'Click on Communication',
        eventAction: 'Launch',
        eventCategory: 'Communication',
        selector: "[data-analytics='nav-communication']",
    },
    {
        action: 'Click on Unread Messages bell icon',
        eventAction: 'Launch',
        eventCategory: 'unreadMessages',
        selector: "[data-analytics='nav-unread-messages']",
    },
    {
        action: 'Click on Revert to original experience button',
        eventAction: 'Launch',
        eventCategory: 'revertToOriginalExperience',
        selector: "[data-analytics='nav-revert-new-faculty-ui']",
    },
];
